import { useSyncExternalStore } from 'react';

/**
 * Ширина бокового меню: с подписями или одними значками.
 *
 * По умолчанию решает ширина окна — от 1200 px подписи помещаются, ниже
 * нет. Но выбор всё равно за человеком: кому-то важнее место под ленту,
 * кому-то чтобы разделы читались. Явное решение перекрывает умолчание и
 * запоминается.
 *
 * Не контекст, а хранилище: состояние нужно и самому `App`, который иначе
 * оказался бы снаружи собственного провайдера, и разделам админки, и
 * стилям через признак на корне документа. Провайдер ради этого пришлось
 * бы поднимать выше App и делить компонент надвое — ради одной булевой
 * величины цена несоразмерная.
 */

const KEY = 'nav-wide';
const ROOMY = '(min-width: 1200px)';

/** `null` — человек не выбирал, решает ширина окна. */
let choice: boolean | null = read();
const listeners = new Set<() => void>();

function read(): boolean | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    // Приватный режим и запрет хранилища: работаем без запоминания.
    return null;
  }
}

function roomy(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(ROOMY).matches;
}

function current(): boolean {
  return choice ?? roomy();
}

function emit(): void {
  // Признак на корне документа: раскладку задаёт CSS, а он должен знать
  // о выборе. Пишем здесь, а не в компоненте, чтобы состояние и его
  // отражение в разметке не могли разойтись.
  document.documentElement.dataset.nav = current() ? 'wide' : 'narrow';
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/**
 * Смена ширины окна меняет умолчание — но только пока человек не выбрал
 * сам. Подписка одна на всё приложение, вешается при первом обращении.
 */
if (typeof window !== 'undefined') {
  window.matchMedia(ROOMY).addEventListener('change', () => {
    if (choice === null) emit();
  });
  document.documentElement.dataset.nav = current() ? 'wide' : 'narrow';
}

/**
 * Переключение всегда задаёт явный выбор, даже если он совпал с
 * умолчанием: иначе после сворачивания и разворота обратно состояние
 * «вернулось к автоматике», и на экране поуже панель схлопнулась бы сама,
 * хотя человек её только что открыл.
 */
export function toggleNavWidth(): void {
  choice = !current();
  try {
    localStorage.setItem(KEY, choice ? '1' : '0');
  } catch {
    // Не запомнилось — не беда: на этой сессии выбор всё равно работает.
  }
  emit();
}

export function useNavWide(): boolean {
  return useSyncExternalStore(subscribe, current, () => false);
}
